/**
 * Kontoer for omsetning og lønn (#183 phase 3): which of PowerOffice Go's accounts in the 3000s are this
 * store's revenue and which in the 5000s its labour cost, for «Hent omsetning fra PowerOffice» and the
 * budget. Every account is chosen until the administrator narrows it; choosing all again is the default.
 * Nothing changes before Lagre, and nothing is written to PowerOffice.
 */
import { errorText, get, put } from '@/api/client';
import { T } from '@/i18n';
import { Button, toast } from '@/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { POWEROFFICE, type PayrollSystem } from './payrollSystem';

interface Account {
  number: number;
  name: string;
  chosen: boolean;
}
interface AccountMap {
  revenue: Account[];
  labour: Account[];
  custom: { revenue: boolean; labour: boolean };
}
type Side = 'revenue' | 'labour';

export function PowerOfficeAccounts({
  onClose,
  sys = POWEROFFICE,
}: { onClose: () => void; sys?: PayrollSystem }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [sys.key, 'account-map'],
    queryFn: () => get<AccountMap>(`${sys.base}/account-map`),
    staleTime: 0,
  });
  const [pick, setPick] = useState<Record<Side, Set<number>> | null>(null);
  useEffect(() => {
    if (q.data && !pick)
      setPick({
        revenue: new Set(q.data.revenue.filter((a) => a.chosen).map((a) => a.number)),
        labour: new Set(q.data.labour.filter((a) => a.chosen).map((a) => a.number)),
      });
  }, [q.data, pick]);
  const save = useMutation({
    mutationFn: () =>
      put<AccountMap>(`${sys.base}/account-map`, {
        revenue: [...(pick?.revenue ?? [])],
        labour: [...(pick?.labour ?? [])],
      }),
    onSuccess: (r) => {
      qc.setQueryData([sys.key, 'account-map'], r);
      qc.invalidateQueries({ queryKey: [sys.key, 'accounts'] });
      qc.invalidateQueries({ queryKey: [sys.key, 'budget'] });
      toast(T('Kontoene er lagret.'));
      onClose();
    },
    onError: (e) => toast(errorText(e)),
  });
  const flip = (side: Side, n: number) => {
    if (!pick) return;
    const s = new Set(pick[side]);
    if (s.has(n)) s.delete(n);
    else s.add(n);
    setPick({ ...pick, [side]: s });
  };
  const list = (side: Side, title: string, accounts: Account[]) => (
    <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'grid', gap: 4 }}>
      <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{title}</legend>
      {accounts.length === 0 && (
        <span style={{ color: 'var(--ink3)', fontSize: 13 }}>
          {T('Ingen kontoer i {system}.', { system: sys.short })}
        </span>
      )}
      {accounts.map((a) => (
        <label key={a.number} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={pick?.[side].has(a.number) ?? false}
            onChange={() => flip(side, a.number)}
          />
          <span>
            {a.number} {a.name}
          </span>
        </label>
      ))}
    </fieldset>
  );
  const d = q.data;
  const empty = !pick || pick.revenue.size === 0 || pick.labour.size === 0;
  return (
    <section data-testid="po-account-map" style={{ display: 'grid', gap: 12, marginTop: 6 }}>
      <div>
        <b style={{ fontSize: 15 }}>{T('Kontoer for omsetning og lønn')}</b>
        <p style={{ margin: '4px 0 0', color: 'var(--ink3)', fontSize: 13 }}>
          {T(
            'Velg hvilke kontoer i {system} som er butikkens omsetning og lønnskostnad. Alle er valgt til du endrer det.',
            { system: sys.short },
          )}
        </p>
      </div>
      {q.isPending ? (
        <output style={{ color: 'var(--ink3)' }}>
          {T('Henter kontoplanen fra {system} …', { system: sys.short })}
        </output>
      ) : q.isError || !d ? (
        <span>{errorText(q.error)}</span>
      ) : (
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}
        >
          {list('revenue', T('Omsetning'), d.revenue)}
          {list('labour', T('Lønnskostnad'), d.labour)}
        </div>
      )}
      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button tone="pri" onClick={() => save.mutate()} disabled={save.isPending || empty}>
          {save.isPending ? T('Lagrer…') : T('Lagre')}
        </Button>
        <Button onClick={onClose} disabled={save.isPending}>
          {T('Lukk')}
        </Button>
      </span>
    </section>
  );
}
